import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { collection, query, onSnapshot, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useNexoraXLedger } from '../lib/NexoraXLedgerManager';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { 
  Coins, TrendingUp, Heart, Scale, Building, ShieldCheck, 
  RefreshCw, CheckCircle, Clock, AlertCircle, FileText, Zap, 
  ArrowUpRight, ArrowDownRight, Database, DownloadCloud, Landmark,
  Filter, Search, Plus, Trash2, Send, Pocket, ToggleLeft, ToggleRight
} from 'lucide-react';

// Interfaces for our dual-mesh data architecture
interface DealRecord {
  id: string;
  clientName: string;
  companyName: string;
  contactEmail: string;
  contactPhone: string;
  serviceSold: string;
  dealValue: string;
  executionNode: string;
  status: string;
  date: string;
  ownerId: string;
}

interface ClientRecord {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  service?: string;
  value: string | number;
  status?: string;
  lastContact?: string;
}

interface BankFeedEntry {
  id: string;
  timestamp: string;
  type: 'CREDIT' | 'DEBIT' | 'TAX_WITHHELD' | 'LEVY_EXTRACTION' | 'NET_SETTLEMENT';
  amount: number;
  reference: string;
  status: 'SETTLED' | 'PENDING' | 'RECONCILED';
  channel: string;
}

export function RealTimeRevenueDashboard() {
  const { metrics, loading: ledgerLoading } = useNexoraXLedger();
  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSync, setLastSync] = useState<Date>(new Date());
  
  // Searching & Filtering State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'SETTLED' | 'PIPELINE'>('ALL');
  
  // Live Node Telemetry Connection states
  const [isSyncActive, setIsSyncActive] = useState(true);

  // New Deal drafting form
  const [isCreatingDeal, setIsCreatingDeal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newServiceSold, setNewServiceSold] = useState('NexoraX Cloud Platform');
  const [newDealValue, setNewDealValue] = useState('18500');
  const [newStatus, setNewStatus] = useState<'PENDING' | 'STAGED' | 'SETTLED'>('STAGED');

  // Trigger feedback toasts
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'success') => {
    setToast({ message: msg, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Helper: parse any pricing metadata into numeric floats
  const parseNumericValue = (valStr: string | number | undefined): number => {
    if (valStr === undefined || valStr === null) return 0;
    if (typeof valStr === 'number') return valStr;
    const cleaned = valStr.replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Firestore Listeners
  useEffect(() => {
    setLoading(true);
    
    // 1. Real-time sub to 'deals'
    const dealsQuery = query(collection(db, 'deals'));
    const unsubscribeDeals = onSnapshot(dealsQuery, (snapshot) => {
      const dealsList: DealRecord[] = [];
      snapshot.forEach((doc) => {
        dealsList.push({ id: doc.id, ...doc.data() } as DealRecord);
      });
      setDeals(dealsList);
      setLastSync(new Date());
    }, (err) => {
      console.error('Error listening to deals node:', err);
      setError('Live gateway connection to Firestore Deals Node failed.');
    });

    // 2. Real-time sub to 'clients' (acting as live direct corporate invoices in Nexorax framework)
    const clientsQuery = query(collection(db, 'clients'));
    const unsubscribeClients = onSnapshot(clientsQuery, (snapshot) => {
      const clientsList: ClientRecord[] = [];
      snapshot.forEach((doc) => {
        clientsList.push({ id: doc.id, ...doc.data() } as ClientRecord);
      });
      setClients(clientsList);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to clients node:', err);
      setError('Live gateway connection to Firestore Clients Node failed.');
      setLoading(false);
    });

    return () => {
      unsubscribeDeals();
      unsubscribeClients();
    };
  }, []);

  // Handle direct state transitions via NexoraX controls (instant database updates)
  const handleSettleDeal = async (deal: DealRecord) => {
    try {
      showToast(`Shifting ${deal.companyName} to SETTLED state...`, 'info');
      await setDoc(doc(db, 'deals', deal.id), {
        ...deal,
        status: 'SETTLED'
      });
      showToast(`Completed! ${deal.companyName} settled successfully. Levy extracted.`, 'success');
    } catch (err) {
      console.error('Failed to change status:', err);
      showToast('Error executing state transition.', 'error');
    }
  };

  const handleDeleteDeal = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to purge deal agreement for "${name}" from the ledger?`)) return;
    try {
      await deleteDoc(doc(db, 'deals', id));
      showToast(`Covenant of ${name} permanently purged from historical tracking.`, 'success');
    } catch (err) {
      console.error(err);
      showToast('Purge script halted.', 'error');
    }
  };

  const handleCreateDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClientName || !newDealValue) {
      showToast('All core credentials required.', 'error');
      return;
    }

    const uniqueId = `DEAL-${Date.now()}`;
    const formattedVal = `£${parseFloat(newDealValue).toLocaleString()}`;
    const cleanRecord: DealRecord = {
      id: uniqueId,
      clientName: newClientName,
      companyName: newCompanyName || 'NexoraX Digital Client',
      contactEmail: `${newClientName.toLowerCase().replace(/\s+/g, '')}@nexorax.co.uk`,
      contactPhone: '08000 622827',
      serviceSold: newServiceSold,
      dealValue: formattedVal,
      executionNode: 'NODE-07-SecurePayment',
      status: newStatus,
      date: new Date().toISOString().split('T')[0],
      ownerId: 'DIRECTOR_SECURE_GATEWAY'
    };

    try {
      showToast('Broadcasting new covenant credentials to nodes...', 'info');
      await setDoc(doc(db, 'deals', uniqueId), cleanRecord);
      showToast('New deal successfully registered and mirrored in the NexoraX Resource Library!', 'success');
      setNewClientName('');
      setNewCompanyName('');
      setIsCreatingDeal(false);
    } catch (err) {
      console.error(err);
      showToast('Deal registration failed.', 'error');
    }
  };

  // Combine and sort total financial records for direct display on our real-time feed cards
  const mergedItems = [
    ...deals.map(d => ({
      id: d.id,
      name: d.clientName,
      source: 'Deals Registry Pipeline',
      company: d.companyName,
      service: d.serviceSold,
      amount: parseNumericValue(d.dealValue),
      status: d.status,
      timestamp: d.date,
      rawItem: d,
      type: 'DEAL'
    })),
    ...clients.map(c => ({
      id: c.id,
      name: c.name || 'Enterprise Ecosystem Partner',
      source: 'Direct Corporate Invoice',
      company: c.name || 'Nexorax Node Participant',
      service: c.service || 'Consultation Allocation',
      amount: parseNumericValue(c.value),
      status: 'SETTLED',
      timestamp: c.lastContact || new Date().toISOString().split('T')[0],
      rawItem: c,
      type: 'INVOICE'
    }))
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Financial Calculations synchronized globally via Centralized NexoraXLedgerManager
  const totalGrossHarvest = metrics.totalGrossHarvest;
  const pipelineDealsSum = metrics.pipelineAmount;
  const NexoraXPerpetualLevy = metrics.NexoraXLevy;
  const splitDunfermline = metrics.dunfermlineSplit;
  const splitGlobal = metrics.globalSplit;
  const splitUK = metrics.ukSplit;
  const lifetimeTaxWithholding = metrics.taxReserve;
  const netNexoraXfinance = metrics.netSettlement;
  const realTimeSecurePaymentReserveTotal = metrics.SecurePaymentReserveTotal;

  // Recharts Chrono Data Plot Construction - PURELY DYNAMIC representation of individual records
  const chronologicalItems = [...mergedItems]
    .filter(item => item.status === 'SETTLED')
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  let runningGross = 0;
  const dynamicTimelineData = chronologicalItems.map((item, idx) => {
    runningGross += item.amount;
    const runningLevy = runningGross * 0.01;
    const runningPostLevy = runningGross - runningLevy;
    const runningTax = runningPostLevy * 0.20;
    const runningSecurePaymentNet = runningPostLevy - runningTax;

    const label = item.company?.length > 12 ? `${item.company.substring(0, 10)}...` : item.company || `Tx #${idx + 1}`;
    return {
      day: label,
      Rec: runningGross,
      Tax: runningTax,
      Levy: runningLevy,
      SecurePayment: runningSecurePaymentNet
    };
  });

  const timelineData = dynamicTimelineData.length > 0 ? dynamicTimelineData : [
    { day: 'Empty Ledger', Rec: 0, Tax: 0, Levy: 0, SecurePayment: 0 }
  ];

  const pieDistributionData = [
    { name: 'Dunfermline Projects', value: splitDunfermline, color: '#f59e0b' },
    { name: 'Global Mission Grid', value: splitGlobal, color: '#10b981' },
    { name: 'UK Strategic Social Funds', value: splitUK, color: '#3b82f6' }
  ];

  // 100% Real-time dynamic banking ledger webhook records derived entirely from the active database records
  const dynamicSecurePaymentFeeds = mergedItems
    .filter(item => item.status === 'SETTLED')
    .flatMap(item => {
      const gross = item.amount;
      const levy = gross * 0.01;
      const levySplit = levy / 3;
      const remains = gross - levy;
      const tax = remains * 0.20;
      const net = remains - tax;

      return [
        {
          id: `FEED-CREDIT-${item.id}`,
          timestamp: item.timestamp,
          type: 'CREDIT' as const,
          amount: gross,
          reference: `RECON CORPCREDIT ${item.company.toUpperCase()}`,
          status: 'SETTLED' as const,
          channel: 'Merchant Gateway'
        },
        {
          id: `FEED-LEVY-1-${item.id}`,
          timestamp: item.timestamp,
          type: 'LEVY_EXTRACTION' as const,
          amount: levySplit,
          reference: `LEVY SPLIT DUNFERMLINE`,
          status: 'RECONCILED' as const,
          channel: 'Splitter Unit'
        },
        {
          id: `FEED-LEVY-2-${item.id}`,
          timestamp: item.timestamp,
          type: 'LEVY_EXTRACTION' as const,
          amount: levySplit,
          reference: `LEVY SPLIT GLOBAL MISSION`,
          status: 'RECONCILED' as const,
          channel: 'Splitter Unit'
        },
        {
          id: `FEED-LEVY-3-${item.id}`,
          timestamp: item.timestamp,
          type: 'LEVY_EXTRACTION' as const,
          amount: levySplit,
          reference: `LEVY SPLIT UK WORK`,
          status: 'RECONCILED' as const,
          channel: 'Splitter Unit'
        },
        {
          id: `FEED-TAX-${item.id}`,
          timestamp: item.timestamp,
          type: 'TAX_WITHHELD' as const,
          amount: tax,
          reference: `TAX WITHHELD BY GAVEL`,
          status: 'SETTLED' as const,
          channel: 'HMRC Real Time Conduit'
        },
        {
          id: `FEED-NET-${item.id}`,
          timestamp: item.timestamp,
          type: 'NET_SETTLEMENT' as const,
          amount: net,
          reference: `NET DEPOSIT Secure Payment Provider`,
          status: 'SETTLED' as const,
          channel: 'Acc: Secure Billing Provider'
        }
      ];
    })
    // Sort recent feeds on top
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // Filter & Search Logic
  const filteredLedgerItems = mergedItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.service.toLowerCase().includes(searchTerm.toLowerCase());
    if (statusFilter === 'ALL') return matchesSearch;
    if (statusFilter === 'SETTLED') return matchesSearch && item.status === 'SETTLED';
    if (statusFilter === 'PIPELINE') return matchesSearch && item.status !== 'SETTLED';
    return matchesSearch;
  });

  return (
    <div id="real-time-revenue-dashboard-root" className="w-full bg-[#030712] border border-cyan-500/20 rounded-3xl p-6 sm:p-8 space-y-8 select-text text-slate-100 relative overflow-hidden shadow-[0_0_50px_rgba(6,182,212,0.05)]">
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-700/5 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-700/5 blur-[120px] rounded-full pointer-events-none"></div>

      {/* Floating feedback toast if active */}
      <AnimatePresence>
        {toast && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-6 right-6 z-50 px-5 py-3.5 rounded-xl border flex items-center gap-3 backdrop-blur-md shadow-2xl ${
              toast.type === 'success' ? 'bg-emerald-950/85 border-emerald-500/40 text-emerald-300' :
              toast.type === 'error' ? 'bg-rose-950/85 border-rose-500/40 text-rose-300' :
              'bg-cyan-950/85 border-cyan-500/40 text-cyan-300'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <AlertCircle className="w-5 h-5" />}
            <span className="text-xs font-mono font-bold tracking-wide">{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MANDATORY OPENING - Preamble praise sequence */}
      <div id="prop-religious-preamble" className="bg-gradient-to-r from-amber-950/20 via-[#0a0f24] to-slate-950 border border-amber-500/20 rounded-2xl px-5 py-3.5 shadow-md flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 text-amber-500">
            <Landmark className="w-4 h-4" />
          </div>
          <div>
            <span id="praise-txt-verified" className="text-xs sm:text-sm font-serif font-semibold text-amber-500/90 italic tracking-wide">
              "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
            </span>
            <span className="block text-[8px] font-mono text-slate-500 uppercase tracking-widest mt-0.5">
              ISLAMIC FINANCIAL PROTOCOLS SYNTAX VERIFIED BY NODE-17
            </span>
          </div>
        </div>
        <div className="hidden sm:block">
          <span className="text-[9px] font-mono font-black text-amber-500 bg-amber-950/20 border border-amber-500/15 rounded px-2 py-1 uppercase tracking-wider">
            ZAKAT ALLOY
          </span>
        </div>
      </div>

      {/* Main Header / Control Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/5 pb-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono font-bold text-cyan-400 uppercase tracking-[0.25em] bg-cyan-950/30 px-2 py-0.5 rounded border border-cyan-500/20">
              SYSTEM OVERVIEW NODE-07
            </span>
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-sans font-black text-white tracking-tight uppercase mt-2">
            Real-Time Revenue Dashboard
          </h2>
          <p className="text-xs font-mono text-slate-400 mt-1 uppercase tracking-wider">
            Enterprise Deal Registry Pipelines & Live Liquidity Audits in Direct Sync with Secure Payment Provider
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsSyncActive(!isSyncActive)}
            className={`px-3 py-1.5 rounded-lg border font-mono text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5 transition-all cursor-pointer ${
              isSyncActive 
                ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-400' 
                : 'bg-slate-900 border-white/10 text-slate-400'
            }`}
          >
            {isSyncActive ? (
              <>
                <Send className="w-3" /> Live Secure Payment Provider API Loop [ON]
              </>
            ) : (
              <>
                <ShieldCheck className="w-3" /> Live Secure Payment Provider API Loop [OFF]
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setIsCreatingDeal(!isCreatingDeal)}
            className="px-4 py-1.5 bg-cyan-500 hover:bg-cyan-400 text-black font-mono text-[10px] font-black uppercase tracking-widest rounded-lg transition-transform hover:scale-105 inline-flex items-center gap-1.5 cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" /> Direct Register Deal
          </button>
        </div>
      </div>

      {/* Interactive Deal Creator Popup Form */}
      <AnimatePresence>
        {isCreatingDeal && (
          <motion.form 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleCreateDeal}
            className="bg-[#080f24] border border-cyan-500/30 rounded-2xl p-5 text-left space-y-4 shadow-xl overflow-hidden"
          >
            <div className="flex justify-between items-center border-b border-cyan-500/10 pb-2">
              <span className="text-[10px] font-mono font-extrabold text-cyan-400 uppercase tracking-widest">
                SIGN COVENANT // ADD TO PIPELINE
              </span>
              <button 
                type="button" 
                onClick={() => setIsCreatingDeal(false)} 
                className="text-slate-400 hover:text-white font-mono text-xs cursor-pointer"
              >
                [CANCEL]
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-[9px] font-mono font-bold text-slate-400 uppercase mb-1">
                  Client/Contract Owner Name
                </label>
                <input 
                  type="text" 
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="e.g. Johnathan Miller"
                  className="w-full bg-[#030612] border border-cyan-500/20 rounded p-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                />
              </div>

              <div>
                <label className="block text-[9px] font-mono font-bold text-slate-400 uppercase mb-1">
                  Company / Organization
                </label>
                <input 
                  type="text" 
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder="e.g. Imperial Energy Group"
                  className="w-full bg-[#030612] border border-cyan-500/20 rounded p-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                />
              </div>

              <div>
                <label className="block text-[9px] font-mono font-bold text-slate-400 uppercase mb-1">
                  Premium Service Allocation
                </label>
                <select 
                  value={newServiceSold}
                  onChange={(e) => setNewServiceSold(e.target.value)}
                  className="w-full bg-[#030612] border border-cyan-500/20 rounded p-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                >
                  <option value="NexoraX Cloud Platform">NexoraX Cloud Platform</option>
                  <option value="NexoraX QR Generator integration">NexoraX QR Generator integration</option>
                  <option value="Nexorax Studio Enterprise License">Nexorax Studio Enterprise License</option>
                  <option value="Secure Invoice Register Infrastructure Service Plan">Secure Invoice Register Infrastructure Service Plan</option>
                </select>
              </div>

              <div>
                <label className="block text-[9px] font-mono font-bold text-slate-400 uppercase mb-1">
                  Deal Value (GBP £)
                </label>
                <div className="relative">
                  <span className="absolute left-2.5 top-2 text-xs text-slate-500 font-mono">£</span>
                  <input 
                    type="number" 
                    value={newDealValue}
                    onChange={(e) => setNewDealValue(e.target.value)}
                    placeholder="e.g. 18500"
                    className="w-full bg-[#030612] border border-cyan-500/20 rounded pl-6 pr-2 py-2 text-xs text-white focus:outline-none focus:border-cyan-400 font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center bg-[#030612]/60 p-3 rounded-xl border border-white/5">
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-[9px] font-mono font-bold text-slate-400 uppercase cursor-pointer">
                  <input 
                    type="radio" 
                    name="st-staged" 
                    checked={newStatus === 'STAGED'} 
                    onChange={() => setNewStatus('STAGED')} 
                    className="accent-cyan-500"
                  />
                  <span>STAGED IN PIPELINE</span>
                </label>

                <label className="flex items-center gap-2 text-[9px] font-mono font-bold text-slate-400 uppercase cursor-pointer">
                  <input 
                    type="radio" 
                    name="st-settled" 
                    checked={newStatus === 'SETTLED'} 
                    onChange={() => setNewStatus('SETTLED')} 
                    className="accent-cyan-500"
                  />
                  <span>SETTLED IMMEDIATELY</span>
                </label>
              </div>

              <button 
                type="submit" 
                className="px-5 py-1.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-black font-mono text-[9px] font-bold uppercase tracking-widest rounded transition-transform hover:scale-105 cursor-pointer"
              >
                PRODUCE LEDGER RECORD
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Real-time Numeric NexoraX Multi-Stage Protocols Grid */}
      <div id="financials-protocols-hero-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        
        {/* Total Gross Harvest */}
        <div className="bg-[#080d1e] border border-slate-800 rounded-2xl p-5 text-left relative overflow-hidden group hover:border-cyan-500/30 transition-all">
          <div className="absolute -top-3 -right-3 w-16 h-16 bg-cyan-500/5 blur-xl rounded-full"></div>
          <div className="flex justify-between items-start">
            <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-widest block">
              01. Gross Harvest
            </span>
            <div className="p-1.5 rounded bg-cyan-950/50 border border-cyan-500/20 text-cyan-400">
              <Database className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-mono font-black text-white tracking-tight block">
              £{totalGrossHarvest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest block mt-1.5">
              Live Verified Settlements
            </span>
          </div>
        </div>

        {/* 1% 3% Community Allocation */}
        <div className="bg-[#080d1e] border border-slate-800 rounded-2xl p-5 text-left relative overflow-hidden group hover:border-amber-500/30 transition-all">
          <div className="absolute -top-3 -right-3 w-16 h-16 bg-amber-500/5 blur-xl rounded-full"></div>
          <div className="flex justify-between items-start">
            <span className="text-[9px] font-mono text-amber-500 font-bold uppercase tracking-widest block">
              02. 3% Community Allocation (1%)
            </span>
            <div className="p-1.5 rounded bg-amber-950/50 border border-amber-500/20 text-amber-400">
              <Heart className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-mono font-black text-amber-500 tracking-tight block">
              £{NexoraXPerpetualLevy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest block mt-1.5">
              Targeted Fund Extraction
            </span>
          </div>
        </div>

        {/* 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) */}
        <div className="bg-[#080d1e] border border-slate-800 rounded-2xl p-5 text-left relative overflow-hidden group hover:border-purple-500/30 transition-all">
          <div className="absolute -top-3 -right-3 w-16 h-16 bg-purple-500/5 blur-xl rounded-full"></div>
          <div className="flex justify-between items-start">
            <span className="text-[9px] font-mono text-purple-400 font-bold uppercase tracking-widest block">
              03. 10% Maintenance & Hosting Fee (20%)
            </span>
            <div className="p-1.5 rounded bg-purple-950/30 border border-purple-500/25 text-purple-300">
              <Scale className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-mono font-black text-purple-400 tracking-tight block">
              £{lifetimeTaxWithholding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest block mt-1.5">
              Withheld at Gavel Node
            </span>
          </div>
        </div>

        {/* Staged Pipeline Asset */}
        <div className="bg-[#080d1e] border border-slate-800 rounded-2xl p-5 text-left relative overflow-hidden group hover:border-indigo-500/30 transition-all">
          <div className="absolute -top-3 -right-3 w-16 h-16 bg-indigo-500/5 blur-xl rounded-full"></div>
          <div className="flex justify-between items-start">
            <span className="text-[9px] font-mono text-indigo-400 font-bold uppercase tracking-widest block">
              04. Staged Pipeline
            </span>
            <div className="p-1.5 rounded bg-indigo-950/30 border border-indigo-500/25 text-indigo-300">
              <TrendingUp className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-mono font-black text-indigo-400 tracking-tight block">
              £{pipelineDealsSum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest block mt-1.5">
              Pipeline Active Flux
            </span>
          </div>
        </div>

        {/* Live SecurePayment Liquid Wallet Cash Balance */}
        <div className="bg-gradient-to-br from-[#022a15]/30 to-[#030c1e] border border-emerald-500/30 rounded-2xl p-5 text-left relative overflow-hidden group hover:border-emerald-400/55 transition-all">
          <div className="absolute -right-2 -bottom-2 w-20 h-20 bg-emerald-500/10 blur-2xl rounded-full"></div>
          <div className="flex justify-between items-start">
            <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase tracking-widest block">
              05. SecurePayment Cash Reserves
            </span>
            <div className="p-1.5 rounded bg-emerald-950/40 border border-emerald-500/30 text-emerald-400">
              <Landmark className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-2xl font-mono font-black text-emerald-400 tracking-tight block">
              £{realTimeSecurePaymentReserveTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-[8px] font-mono text-slate-400 uppercase tracking-widest block mt-1.5">
              SecurePayment: Account Secure Billing Provider
            </span>
          </div>
        </div>
      </div>

      {/* Visual Analytics Charts & Levy Split Map */}
      <div id="recharts-visuals-split" className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
        {/* Main Area Chart spanning 8 cols on desktop */}
        <div className="lg:col-span-8 bg-[#04081c]/60 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between space-y-4">
          <div className="flex justify-between items-center border-b border-white/5 pb-3">
            <div>
              <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-widest block">
                NEXORAX LEDGER GROWTH CHRONOMETRICS
              </span>
              <h3 className="text-sm font-bold text-white uppercase mt-0.5">
                Financial Accumulation Index
              </h3>
            </div>
            <span className="text-[8px] font-mono text-slate-500 uppercase">
              SecurePayment Integration Loop Sync
            </span>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSecurePayment" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorGross" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" opacity={0.3} />
                <XAxis dataKey="day" stroke="#64748b" fontSize={9} fontFamily="monospace" />
                <YAxis stroke="#64748b" fontSize={9} fontFamily="monospace" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#020617', borderColor: 'rgba(6,182,212,0.2)', fontSize: 11, fontFamily: 'monospace' }}
                  labelStyle={{ color: '#06b6d4' }}
                />
                <Area type="monotone" dataKey="Rec" name="Total Gross (£)" stroke="#06b6d4" fillOpacity={1} fill="url(#colorGross)" strokeWidth={2.5} />
                <Area type="monotone" dataKey="SecurePayment" name="Net Liquid Reserves (£)" stroke="#10b981" fillOpacity={1} fill="url(#colorSecurePayment)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 1% 3% Community Allocation split visualization */}
        <div className="lg:col-span-4 bg-[#04081c]/60 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between">
          <div className="border-b border-white/5 pb-3">
            <span className="text-[9px] font-mono text-amber-500 font-bold uppercase tracking-widest block">
              1% LEVY SPLIT GEOMETRIC MAP
            </span>
            <h3 className="text-sm font-bold text-white uppercase mt-0.5">
              3-Way Ingress Dispatch Rules
            </h3>
          </div>

          <div className="h-44 w-full flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#020617', border: '1px solid rgba(245,158,11,0.2)', fontSize: 10, fontFamily: 'monospace' }} 
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center value overlay */}
            <div className="absolute flex flex-col items-center justify-center font-mono">
              <span className="text-[8px] text-slate-500 uppercase tracking-widest">Global Levy</span>
              <span className="text-xs font-black text-amber-500">
                £{NexoraXPerpetualLevy.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>

          {/* Allocation Legends */}
          <div className="space-y-2 mt-2">
            {pieDistributionData.map((allocation, i) => (
              <div key={i} className="flex justify-between items-center text-[10px] font-mono border-t border-white/5 pt-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: allocation.color }}></div>
                  <span className="text-slate-400 capitalize">{allocation.name.split(' ')[0]} split:</span>
                </div>
                <span className="text-white font-bold">
                  £{allocation.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Dual-Flux Ledger & SecurePayment Open Banking Bank Feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 text-left">
        
        {/* Left Side: Interactive Flux Ledger */}
        <div id="interactive-flux-ledger-mesh" className="lg:col-span-8 bg-[#04081c]/40 border border-slate-800 rounded-2xl p-5 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 border-b border-white/5 pb-4">
            <div>
              <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-widest block">
                INTEGRATED INTERACTIVE TRANSCRIPTION MESH
              </span>
              <h3 className="text-sm font-bold text-white uppercase mt-0.5">
                Covenant Ledgers &amp; Direct Settlements
              </h3>
            </div>

            {/* Filter tools */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2" />
                <input
                  type="text"
                  placeholder="Filter client or organization..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-black/40 border border-white/10 rounded-lg pl-8 pr-2 py-1 text-[10px] text-white focus:outline-none focus:border-cyan-500/50 font-mono w-40 sm:w-48"
                />
              </div>

              <div className="flex bg-black/40 border border-white/10 rounded-lg p-0.5 font-mono text-[9px] text-slate-500">
                <button
                  type="button"
                  onClick={() => setStatusFilter('ALL')}
                  className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${statusFilter === 'ALL' ? 'bg-cyan-500/10 text-cyan-400 font-black' : 'hover:text-slate-300'}`}
                >
                  ALL
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('SETTLED')}
                  className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${statusFilter === 'SETTLED' ? 'bg-emerald-500/10 text-emerald-400 font-black' : 'hover:text-slate-300'}`}
                >
                  SETTLED
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('PIPELINE')}
                  className={`px-2 py-0.5 rounded transition-colors cursor-pointer ${statusFilter === 'PIPELINE' ? 'bg-indigo-500/10 text-indigo-400 font-black' : 'hover:text-slate-300'}`}
                >
                  STAGED
                </button>
              </div>
            </div>
          </div>

          {/* List display */}
          <div className="space-y-2.5 max-h-[380px] overflow-y-auto custom-scrollbar pr-1">
            {loading ? (
              <div className="py-12 text-center text-slate-500 font-mono text-xs animate-pulse uppercase tracking-widest">
                Connecting deep blockchain indexes from firestore...
              </div>
            ) : filteredLedgerItems.length === 0 ? (
              <div className="py-16 text-center text-slate-500 font-mono text-xs uppercase border border-dashed border-slate-800 rounded-2xl">
                No verified deal structures matched your active nodes filter parameters.
              </div>
            ) : (
              filteredLedgerItems.map((item) => (
                <div 
                  key={item.id} 
                  className={`p-3.5 bg-slate-950/40 border rounded-xl flex flex-col sm:flex-row justify-between sm:items-center gap-3 transition-colors ${
                    item.status === 'SETTLED' 
                      ? 'border-[#10b981]/25 hover:bg-[#021f10]/15' 
                      : 'border-cyan-500/20 hover:bg-[#050e24]/15'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded mt-0.5 ${
                      item.status === 'SETTLED' ? 'bg-[#022c15]/50 text-emerald-400' : 'bg-cyan-950/40 text-cyan-400'
                    }`}>
                      {item.type === 'INVOICE' ? <FileText className="w-4 h-4" /> : <Coins className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-extrabold text-xs">{item.company}</span>
                        <span className="text-[7.5px] font-mono text-slate-500 uppercase px-1.5 py-0.5 bg-black/40 border border-white/5 rounded">
                          {item.source}
                        </span>
                      </div>
                      <div className="text-[10px] font-mono text-slate-400 mt-1 uppercase">
                        Owner: {item.name} &bull; Service: <span className="text-cyan-400">{item.service}</span>
                      </div>
                      <span className="text-[8px] font-mono text-slate-500 block mt-0.5">
                        REGISTRY ID: {item.id} &bull; TIMESTAMP: {item.timestamp}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-start gap-4">
                    <div className="text-left sm:text-right font-mono">
                      <span className={`text-sm font-black block tracking-wider ${
                        item.status === 'SETTLED' ? 'text-emerald-400' : 'text-cyan-400'
                      }`}>
                        £{item.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className={`text-[8.5px] font-bold uppercase tracking-wider block ${
                        item.status === 'SETTLED' ? 'text-emerald-500/80' : 'text-cyan-500/80'
                      }`}>
                        {item.status}
                      </span>
                    </div>

                    {/* Operational action items */}
                    <div className="flex items-center gap-1.5 bg-black/20 p-1.5 rounded-lg border border-white/5">
                      {item.status !== 'SETTLED' && (
                        <button
                          type="button"
                          onClick={() => handleSettleDeal(item.rawItem as DealRecord)}
                          title="Execute Direct SecurePayment Settlement Clearance"
                          className="px-2 py-1 bg-emerald-500 hover:bg-emerald-400 text-black font-mono text-[8px] font-extrabold uppercase tracking-wider rounded transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <CheckCircle className="w-2.5 h-2.5" /> Settle Transaction
                        </button>
                      )}
                      
                      <button
                        type="button"
                        onClick={() => handleDeleteDeal(item.id, item.company)}
                        title="Purge transaction telemetry"
                        className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-950/20 rounded transition-colors cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Side: SecurePayment Open Banking Live Feeds */}
        <div className="lg:col-span-4 bg-[#04081c]/40 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between space-y-4">
          <div className="border-b border-white/5 pb-3">
            <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-widest block">
              SecurePayment DIRECT API PLUGINS FEED
            </span>
            <h3 className="text-sm font-bold text-white uppercase mt-0.5 flex items-center gap-2">
              <Landmark className="w-4 h-4 text-cyan-400 animate-pulse" /> Open Banking Ledger Webhooks
            </h3>
          </div>

          {/* Bank feeds list */}
          <div className="space-y-2 flex-grow overflow-y-auto max-h-[300px] custom-scrollbar pr-1">
            {!isSyncActive ? (
              <div className="py-20 text-center font-mono text-[9px] text-slate-500 uppercase leading-normal border border-dashed border-slate-900 rounded-xl">
                SecurePayment open banking sync channel disabled.
                <span className="block text-[8px] text-rose-500 mt-2">Active Loop: OFF</span>
              </div>
            ) : dynamicSecurePaymentFeeds.length === 0 ? (
              <div className="py-20 text-center font-mono text-[9px] text-slate-500 uppercase leading-normal border border-dashed border-slate-900 rounded-xl">
                Awaiting incoming open banking telemetry feeds from SecurePayment London Central...
                <span className="block text-[8px] text-cyan-500 mt-2">Active Loop: ON</span>
              </div>
            ) : (
              <div className="space-y-2 text-left">
                {dynamicSecurePaymentFeeds.map((feed) => (
                  <div key={feed.id} className="p-2.5 bg-slate-950/60 border border-white/5 rounded-lg flex justify-between items-center text-[9px] font-mono leading-relaxed">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          feed.type === 'CREDIT' ? 'bg-emerald-400' :
                          feed.type === 'NET_SETTLEMENT' ? 'bg-cyan-400' :
                          feed.type === 'TAX_WITHHELD' ? 'bg-purple-400' :
                          'bg-amber-400'
                        }`}></span>
                        <span className="text-white font-extrabold text-[10px]">{feed.reference.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-slate-500 text-[8px] uppercase block tracking-wider">
                        {feed.channel} &bull; Date: {feed.timestamp}
                      </span>
                    </div>

                    <div className="text-right">
                      <span className={`font-black tracking-wider block ${
                        feed.type === 'CREDIT' ? 'text-emerald-400' :
                        feed.type === 'NET_SETTLEMENT' ? 'text-cyan-400' :
                        feed.type === 'TAX_WITHHELD' ? 'text-purple-400' :
                        'text-amber-400'
                      }`}>
                        {feed.type === 'CREDIT' || feed.type === 'NET_SETTLEMENT' ? '+' : '-'}{feed.amount > 0 ? `£${feed.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '0.00'}
                      </span>
                      <span className="text-[7.5px] text-slate-500 uppercase font-bold">{feed.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-[#0b0c16]/80 border border-white/5 rounded-xl p-3 text-[8px] font-mono text-slate-400 uppercase leading-relaxed text-left">
            <span className="text-cyan-400 font-extrabold block mb-1">
              CloudSync-02 REPLICATION BRIDGE ACTIVE
            </span>
            Continuous live ledger feeds compiled. Mirroring of all SecurePayment reconciliations to Google Drive NexoraX Resource Library validated.
          </div>
        </div>
      </div>
    </div>
  );
}
